import { Button, Popconfirm, Select, Space, Table, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import { DeleteOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { getEquipments } from "../../api/equipment";
import { deleteEquipmentPreCheckAns, getEquipmentPreCheckAns } from "../../api/equipmentPreCheckAns";
import { PrecheckQxnRenderer } from "../../components/PrecheckQxn/precheckQxn";
import dayjs from "dayjs";
import DateRangeFilter from "../../components/shared/DateRangeFilter";
import { PER_PAGE } from "../../configs";
import jsPDF from "jspdf";
import { autoTable } from 'jspdf-autotable'

export function EquipmentPreCheckAns({ user }) {
  const navigate = useNavigate();
  const perPage = PER_PAGE;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const dataIndex = (page - 1) * perPage;

  const [isLoading, setIsLoading] = useState(false);

  const [showCreatePrecheckAns, setShowCreatePrecheckAns] = useState(false);
  const [precheckAns, setPrecheckAns] = useState(null);
  const [ansSet, setAnsSet] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [range, setRange] = useState(null);

  const [equipments, setEquipments] = useState([]);
  const [equipment, setEquipment] = useState(null);

  const fetchEquipments = async ({ page, q }) => {
    try {
      const { results } = await getEquipments({ page, perPage: perPage, q });
      setEquipments(results);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  const fetchEquipmentPreCheckAns = useCallback(async ({ page, equipmentId }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getEquipmentPreCheckAns({
        page,
        perPage,
        equipmentId,
        sortBy: sortField,
        sortType: sortOrder,
        startAt: range?.startDate,
        endAt: range?.endDate,
      });
      setTotal(total);
      setPrecheckAns(results);
      console.log("Precheck answers:\n", results)
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  }, [perPage, range?.endDate, range?.startDate, sortField, sortOrder]);

  const handleDelete = async (ans) => {
    await deleteEquipmentPreCheckAns(ans.id);
    fetchEquipmentPreCheckAns({
      page: 1,
      perPage,
    });
  };

  useEffect(() => {
    fetchEquipmentPreCheckAns({
      page,
      perPage,
    });
  }, [fetchEquipmentPreCheckAns, page, perPage, sortField, sortOrder]);

  useEffect(() => {
    if (!equipment) return;
    setPage(1);
    fetchEquipmentPreCheckAns({
      page: 1,
      equipmentId: equipment.id,
    });
  }, [equipment, fetchEquipmentPreCheckAns]);

  const hanldeTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setSortField(sorter.field);
    setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
  }


  const handleDownload = async (ans) => {
    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);

    // Title
    pdf.setFontSize(18);
    pdf.setTextColor(40, 40, 40); // Dark gray color
    pdf.text("Pre-Check Inspection Answers", 15, 20);

    // Equipment Details
    pdf.setFontSize(12);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Equipment: ${ans.questionSet.equipment.name}`, 15, 30);
    pdf.text(`Type: ${ans.questionSet.equipment.type}`, 15, 40);
    pdf.text(`Submitted By: ${ans.user.firstName} ${ans.user.lastName || ""}`, 15, 50);
    pdf.text(`Date: ${dayjs(ans.createdAt).format("DD MMM YYYY HH:mm A")}`, 15, 60);

    // Data for the table
    const tableData = ans.answers.map((answer, index) => [
      index + 1,
      answer.question.text,
      { content: answer.value, styles: { fontStyle: "bold" } },
    ]);

    autoTable(pdf, {
      startY: 70,
      head: [["#", "Question", "Response"]], // Table headers
      body: tableData, // Table data
      theme: "striped",
      styles: {
        fontSize: 10,
        cellPadding: 5,
        textColor: [40, 40, 40],
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: "auto" },
        2: { cellWidth: "auto" },
      },
    });

    // Save the PDF
    pdf.save(`precheck-${ans.questionSet.equipment.name}-${ans.createdAt}.pdf`);
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Equipment", dataIndex: "equipmentName", key: "equipmentName" },
    { title: 'Type', dataIndex: 'equipmentType', key: 'equipmentType' },
    { title: "Submitted By", dataIndex: "submittedBy", key: "submittedBy" },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
    },
    {
      title: "Action",
      key: "action",
      with: 300,
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete?"
            onConfirm={() => handleDelete(precheckAns[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setAnsSet(precheckAns[index]);
            }}
          ></Button>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(precheckAns[index])}
          />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} className="p-6">
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title="Equipments Precheck Answers"
          subTitle="Precheck answers for equipments"
        />

        <Button type="primary" onClick={() => setShowCreatePrecheckAns(true)}>
          Submit Precheck Questions
        </Button>
      </Space>

      <div className="flex justify-between">
        <Select
          value={equipment ? equipment.id : null}
          placeholder="Search Equipment"
          onSearch={(str) => fetchEquipments({ page: 1, q: str })}
          size="large"
          onChange={(eid) => {
            setEquipment(equipments.find((e) => e.id === eid));
          }}
          filterOption={false}
          options={equipments.map((e) => {
            return {
              value: e.id,
              label: e.name,
            };
          })}
          showSearch
        ></Select>
        <DateRangeFilter
          onChange={({ startDate, endDate }) => {
            if (startDate && endDate) {
              setRange({ startDate, endDate });
            }
          }}
          onClear={() => setRange({ startDate: null, endDate: null })}
        />
      </div>

      <Table
        dataSource={(precheckAns || []).map((ans, index) => {
          return {
            no: dataIndex + index + 1,
            equipmentName: ans.questionSet.equipment.name,
            equipmentType: ans.questionSet.equipment.type,
            submittedBy: ans.user.firstName,
            createdAt: dayjs(ans.createdAt).format("DD MMM YYYY HH:mm A"),
          };
        })}
        columns={TableCols}
        loading={isLoading}
        onChange={hanldeTableChange}
        pagination={{
          size: "small",
          total,
          pageSize: perPage,
          current: parseInt(page),
          onChange: setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${total} Precheck Question Set`,
          position: ["bottomLeft"],
        }}
      />

      {showCreatePrecheckAns || ansSet ? (
        <PrecheckQxnRenderer
          ansSet={ansSet}
          user={user}
          onCancel={() => {
            setShowCreatePrecheckAns(false);
            setAnsSet(null);
          }}
          onSubmit={() => {
            setShowCreatePrecheckAns(false);
            setAnsSet(null);
            fetchEquipmentPreCheckAns({
              page: 1,
              perPage,
            });
          }}
        />
      ) : null}
    </Space>
  );
}
